import {
  UseInfiniteQueryResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  IntegrationContext,
  IntegrationScope,
  IntegrationUser,
} from "../integrations.types";

export type M365CreatedBy = {
  user_id: number;
  email: string;
  full_name: string;
};

export type M365AuthenticatedUser = {
  id: string;
  email: string;
  name: string;
};

export type M365OauthSettings = {
  tokens: {
    access_token: string; // Time in seconds until the access_token expires (usually 3600)
    refresh_token: string; // Used to obtain new access tokens without user interaction; expires in 90 days
    expires_in: number; // seconds
    ext_expires_in: number; // seconds
    scopes: string; // space-separated list of scopes
    token_type: string; // typically "Bearer"
    expires_on: number; // unix timestamp
    id_token: string; // JWT token containing user information
    authenticated_user: M365AuthenticatedUser;
  };
  created_on: string; // when the integration was created
};

export type M365ClientCredentialsSettings = {
  client_credentials: {
    client_id: string;
    client_secret: string;
    tenant_id: string;
  };
  created_on: string;
};

export type M365SaveClientCredentialsData = {
  client_id: string;
  client_secret: string;
  tenant_id: string;
  scope: IntegrationScope;
};

export type M365SaveClientCredentialsResult = {
  data: M365Integration;
};

export type M365SaveClientCredentialsFn = (
  values: M365SaveClientCredentialsData
) => Promise<M365SaveClientCredentialsResult>;

export type M365SaveOAuth2CredentialsData = {
  code: string;
  state: string;
  scope: IntegrationScope;
};

export type M365SaveOAuth2CredentialsResult = {
  data: M365Integration;
};

export type M365SaveOAuth2CredentialsFn = (
  values: M365SaveOAuth2CredentialsData
) => Promise<M365SaveOAuth2CredentialsResult>;

export enum M365AuthentactionMethod {
  CLIENT_CREDENTIALS = "client_credentials",
  OAUTH2 = "oauth2",
}

export type M365Integration = {
  settings: M365ClientCredentialsSettings | M365OauthSettings;
  options: {
    method: M365AuthentactionMethod;
  };
  name: string;
  integration_id: number;
  created_by: M365CreatedBy;
};

export type M365GetIntegrationQuery = {
  integration_id?: number;
  scope?: string;
};

export type M365GetIntegrationResponse = {
  data: M365Integration[];
};

export type M365GetIntegrationFn = (
  query?: M365GetIntegrationQuery
) => Promise<M365GetIntegrationResponse>;

export type M365GetAuthUrlResponse = {
  data: {
    url: string;
  };
};

export type M365GetAuthUrlFn = () => Promise<M365GetAuthUrlResponse>;

/**
 * M365 Graph API
 */

export type M365GetUsersQuery = {
  search?: string;
  page?: string;
  page_size?: number;
  context: IntegrationContext;
};

export type M365GetUsersResponse = {
  data: IntegrationUser[];
  nextPage: string;
  page: string;
  success: boolean;
};

export type M365GetUsersFn = (
  query?: M365GetUsersQuery
) => Promise<M365GetUsersResponse>;

export type M365UseGetUsers = (
  q?: M365GetUsersQuery,
  options?: Omit<
    UseQueryOptions<
      M365GetUsersResponse,
      Error,
      M365GetUsersResponse,
      readonly unknown[]
    >,
    "queryKey" | "queryFn"
  >
) => UseQueryResult<M365GetUsersResponse, Error>;

export type M365UseGetInfiniteUsers = (
  q: M365GetUsersQuery,
  options?: {
    enabled?: boolean;
  }
) => UseInfiniteQueryResult<{
  pages: M365GetUsersResponse[];
  pageParams: number[];
}>;
